import React, { useState } from "react"
import { toast } from "react-toastify"
import {
  previewStudentImport,
  applyStudentImport,
  StudentImportError,
  StudentImportSummary,
  StudentImportApplyResponse,
} from "../../../../../service/student"

interface StudentImportModalProps {
  onClose: () => void
  onImported: () => void
}

type Stage = "select" | "previewing" | "confirm" | "importing" | "result"

const StudentImportModal: React.FC<StudentImportModalProps> = ({ onClose, onImported }) => {
  const [file, setFile] = useState<File | null>(null)
  const [stage, setStage] = useState<Stage>("select")
  const [summary, setSummary] = useState<StudentImportSummary | null>(null)
  const [sessionTitle, setSessionTitle] = useState("")
  const [applyResult, setApplyResult] = useState<StudentImportApplyResponse | null>(null)
  const [error, setError] = useState("")

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFile(e.target.files?.[0] || null)
    setSummary(null)
    setApplyResult(null)
    setError("")
    setStage("select")
  }

  const handlePreview = async () => {
    if (!file) return
    setStage("previewing")
    setError("")
    try {
      const res = await previewStudentImport(file)
      setSummary(res)
      setSessionTitle(res.session)
      setStage("confirm")
    } catch (err) {
      setError(err instanceof StudentImportError ? err.message : "Failed to read the uploaded file")
      setStage("select")
    }
  }

  const handleConfirmImport = async () => {
    if (!file || !summary) return
    setStage("importing")
    setError("")
    try {
      const res = await applyStudentImport(file)
      setApplyResult(res)
      setStage("result")
      toast.success(res.message)
      onImported()
    } catch (err) {
      const message = err instanceof StudentImportError ? err.message : "Failed to import students"
      setError(message)
      toast.error(message)
      setStage("confirm")
    }
  }

  const handleStartOver = () => {
    setFile(null)
    setSummary(null)
    setApplyResult(null)
    setError("")
    setStage("select")
  }

  const busy = stage === "previewing" || stage === "importing"

  return (
    <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h4 className="modal-title">Upload Students Excel</h4>
            <button type="button" className="btn-close custom-btn-close" onClick={onClose} disabled={busy}>
              <i className="ti ti-x" />
            </button>
          </div>

          <div className="modal-body">
            {error && <div className="alert alert-danger">{error}</div>}

            {stage === "result" && applyResult ? (
              <div>
                <div className="alert alert-success mb-3">{applyResult.message}</div>
                <ul className="list-unstyled mb-0">
                  <li>Created: {applyResult.stats.created}</li>
                  <li>Already present (skipped): {applyResult.stats.skipped}</li>
                  <li>From another campus (skipped): {applyResult.stats.skippedWrongCampus}</li>
                  <li>
                    Parents created: {applyResult.stats.parentsCreated}, reused: {applyResult.stats.parentsReused}
                  </li>
                  <li>Discounts created: {applyResult.stats.discountsCreated}</li>
                  <li>Errors: {applyResult.stats.errors.length}</li>
                </ul>
                {applyResult.stats.errors.length > 0 && (
                  <div className="mt-3">
                    <p className="fw-medium mb-1">Rows that couldn't be imported:</p>
                    <div className="border rounded p-2" style={{ maxHeight: 200, overflowY: "auto" }}>
                      {applyResult.stats.errors.map((e) => (
                        <div key={e.gr} className="small text-danger">
                          GR {e.gr} — {e.name}: {e.error}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (stage === "confirm" || stage === "importing") && summary ? (
              <div>
                {summary.mismatchedCount > 0 && (
                  <div className="alert alert-warning">
                    <strong>{summary.mismatchedCount}</strong> of {summary.totalRecords} rows have a GR number
                    that doesn't look like it belongs to Campus {summary.campusNo}
                    {summary.mismatchedSamples[0]?.sheetCampus
                      ? ` (sheet says "${summary.mismatchedSamples[0].sheetCampus}")`
                      : ""}
                    . Those rows will be skipped — only rows that match Campus {summary.campusNo} are imported.
                    <div className="mt-2 small">
                      {summary.mismatchedSamples.slice(0, 5).map((s) => (
                        <div key={s.gr}>
                          GR {s.gr} — {s.name}
                        </div>
                      ))}
                      {summary.mismatchedCount > 5 && <div>…and {summary.mismatchedCount - 5} more</div>}
                    </div>
                  </div>
                )}
                <p className="mb-1">
                  Importing into <strong>Campus {summary.campusNo}</strong>, session{" "}
                  <strong>{sessionTitle}</strong>.
                </p>
                <ul className="list-unstyled mb-0">
                  <li>Total rows in file: {summary.totalRecords}</li>
                  {summary.mismatchedCount > 0 && (
                    <li>From another campus (will be skipped): {summary.mismatchedCount}</li>
                  )}
                  <li>Already present (will be skipped): {summary.alreadyPresentCount}</li>
                  <li>New students to import: {summary.newCount}</li>
                </ul>
              </div>
            ) : (
              <div>
                <label className="form-label">Excel file</label>
                <input
                  type="file"
                  className="form-control"
                  accept=".xlsx"
                  onChange={handleFileChange}
                  disabled={busy}
                />
                <p className="text-muted small mt-2 mb-0">
                  Same format as the Campus-N.xlsx files used for student data imports — first row headers,
                  data from row 2. Only GRs not already in the system will be imported.
                </p>
              </div>
            )}
          </div>

          <div className="modal-footer">
            {stage === "result" ? (
              <>
                <button type="button" className="btn btn-outline-secondary" onClick={handleStartOver}>
                  Upload Another File
                </button>
                <button type="button" className="btn btn-primary" onClick={onClose}>
                  Done
                </button>
              </>
            ) : stage === "confirm" || stage === "importing" ? (
              <>
                <button type="button" className="btn btn-light" onClick={handleStartOver} disabled={busy}>
                  Back
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleConfirmImport}
                  disabled={busy || !summary?.newCount}
                >
                  {stage === "importing"
                    ? "Importing…"
                    : `Import ${summary?.newCount ?? 0} Student${summary?.newCount === 1 ? "" : "s"}`}
                </button>
              </>
            ) : (
              <>
                <button type="button" className="btn btn-light" onClick={onClose} disabled={busy}>
                  Cancel
                </button>
                <button type="button" className="btn btn-primary" onClick={handlePreview} disabled={!file || busy}>
                  {stage === "previewing" ? "Reading…" : "Next"}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default StudentImportModal
